import { useState } from 'react';
import feeService from 'ApiService/Accountant/feeService';
import Nodatafound from 'pages/Common/Nodatafound';
import toast from 'react-hot-toast';

const CoursePaymentHistory = ({ feeDetail }) => (
  <div className="mb-8 rounded-lg border border-gray-200 p-4 dark:border-white/10">
    <div className="mb-4">
      <h5 className="text-lg font-bold text-navy-700 dark:text-white">
        {feeDetail.course} {feeDetail.isSwap && '(Swapped Course)'}
      </h5>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Batch: {feeDetail.batch}
        {feeDetail.originalCourse && (
          <span className="ml-2">
            (Swapped from {feeDetail.originalCourse})
          </span>
        )}
      </p>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full font-dm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-white/10">
            <th className="px-2 py-3 text-start text-xs font-bold tracking-wide text-gray-600">
              Date
            </th>
            <th className="px-2 py-3 text-start text-xs font-bold tracking-wide text-gray-600">
              Paid Amount
            </th>
            <th className="px-2 py-3 text-start text-xs font-bold tracking-wide text-gray-600">
              Payment Type
            </th>
            <th className="px-2 py-3 text-start text-xs font-bold tracking-wide text-gray-600">
              Transaction Number
            </th>
          </tr>
        </thead>
        <tbody>
          {feeDetail.paymentHistory.length > 0 ? (
            feeDetail.paymentHistory.map((row, index) => (
              <tr
                key={index}
                className="border-b border-gray-200 dark:border-white/10"
              >
                <td className="px-2 py-3">
                  <div className="whitespace-nowrap text-sm font-medium text-navy-700 dark:text-white">
                    {new Date(row.date).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-2 py-3">
                  <div className="whitespace-nowrap text-sm font-medium text-navy-700 dark:text-white">
                    {row.amount}
                  </div>
                </td>
                <td className="px-2 py-3">
                  <div className="whitespace-nowrap text-sm font-medium text-navy-700 dark:text-white">
                    {row.paymentType}
                  </div>
                </td>
                <td className="px-2 py-3">
                  <div className="whitespace-nowrap text-sm font-medium text-navy-700 dark:text-white">
                    {row.txnNumber}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan="4"
                className="py-3 text-center text-sm text-gray-700 dark:text-white"
              >
                No payment history available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    <div className="mt-4 flex flex-col gap-2 rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
      <div className="flex justify-between">
        <span className="text-sm text-navy-700 dark:text-white">
          Total Fees:
        </span>
        <span className="font-bold text-navy-700 dark:text-white">
          {feeDetail.isSwap ? feeDetail.swapFee : feeDetail.totalFees}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-navy-700 dark:text-white">Paid:</span>
        <span className="font-bold text-navy-700 dark:text-white">
          {feeDetail.paidFees}
        </span>
      </div>
      <div className="flex justify-between border-t border-gray-200 pt-2 dark:border-white/10">
        <span className="text-sm text-navy-700 dark:text-white">
          Remaining:
        </span>
        {feeDetail.remainingFees <= 0 ? (
          <span className="font-medium text-green-500">Fee Paid</span>
        ) : (
          <span className="font-bold text-navy-700 dark:text-white">
            {feeDetail.remainingFees}
          </span>
        )}
      </div>
    </div>
  </div>
);

const FeeSummary = ({ summary, userName }) => (
  <div className="mt-8 flex flex-col justify-between gap-8 md:flex-row">
    <div className="flex flex-col text-wrap">
      <p className="text-lg font-bold text-navy-700 dark:text-white">
        Fee Summary
      </p>
      <p className="text-lg text-navy-700 dark:text-white">Note</p>
      <p className="w-[290px] text-gray-600">
        Welcome, {userName}! Here you can review and manage the fee details for
        selected students and classes. If you need assistance, feel free to
        reach out. Thank you!
      </p>
    </div>

    <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
      <div className="flex items-center justify-between gap-8">
        <p className="text-sm text-navy-700 dark:text-white">Total Courses:</p>
        <p className="font-bold text-navy-700 dark:text-white">
          {summary.totalCourses}
        </p>
      </div>
      <div className="flex items-center justify-between gap-8">
        <p className="text-sm text-navy-700 dark:text-white">Active Courses:</p>
        <p className="font-bold text-navy-700 dark:text-white">
          {summary.activeCourses}
        </p>
      </div>
      <div className="flex items-center justify-between gap-8">
        <p className="text-sm text-navy-700 dark:text-white">
          Swapped Courses:
        </p>
        <p className="font-bold text-navy-700 dark:text-white">
          {summary.swappedCourses}
        </p>
      </div>
      <div className="h-px w-full bg-gray-200 dark:bg-white/10" />
      <div className="flex items-center justify-between gap-8">
        <p className="text-sm text-navy-700 dark:text-white">Total Fees:</p>
        <p className="font-bold text-navy-700 dark:text-white">
          {summary.totalCourseFees}
        </p>
      </div>
      <div className="flex items-center justify-between gap-8">
        <p className="text-sm text-navy-700 dark:text-white">Total Paid:</p>
        <p className="font-bold text-navy-700 dark:text-white">
          {summary.totalPaidFees}
        </p>
      </div>
      <div className="flex items-center justify-between gap-8">
        <p className="text-sm text-navy-700 dark:text-white">
          Total Remaining:
        </p>
        {summary.totalRemainingFees <= 0 ? (
          <p className="font-medium text-green-500">All Fees Paid</p>
        ) : (
          <p className="font-bold text-navy-700 dark:text-white">
            {summary.totalRemainingFees}
          </p>
        )}
      </div>
    </div>
  </div>
);

const StudentFeeDetails = ({
  students,
  selectedStudent,
  setSelectedStudent,
  state,
}) => {
  const [feeDetails, setFeeDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleStudentSelect = async (event) => {
    const studentId = event.target.value;
    const student = students.find((s) => s._id === studentId);
    setSelectedStudent(student);

    setLoading(true);
    try {
      const response = await feeService.getStudentPaymentDetails(student._id);
      if (response.data.success) {
        setFeeDetails(response.data.data);
      } else {
        toast.error('Failed to fetch fee details');
        setFeeDetails(null);
      }
    } catch (error) {
      toast.error('Error fetching fee details');
      setFeeDetails(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      <h4 className="mb-6 text-lg font-bold text-navy-700 dark:text-white">
        Student Fee Details
      </h4>

      <div className="mb-6 w-full md:w-1/2">
        <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
          Select Student
        </label>
        <select
          onChange={handleStudentSelect}
          value={selectedStudent?._id || ''}
          name="student"
          className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
        >
          <option value="">Click here to Select Student</option>
          {students.length === 0 ? (
            <option>No students available</option>
          ) : (
            students.map((student) => (
              <option key={student._id} value={student._id}>
                {student.studentName}
              </option>
            ))
          )}
        </select>
      </div>

      <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
        <div className="flex justify-between">
          <h4 className="pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
            Payment History
          </h4>
        </div>

        {loading ? (
          <div className="flex h-screen items-center justify-center">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500"></div>
          </div>
        ) : feeDetails ? (
          <div>
            {feeDetails.feeDetails.map((feeDetail, index) => (
              <CoursePaymentHistory key={index} feeDetail={feeDetail} />
            ))}
            <FeeSummary
              summary={feeDetails.summary}
              userName={state?.user?.name}
            />
          </div>
        ) : (
          <p className="mt-0 text-center">
            <Nodatafound title="No Fee data found" />
          </p>
        )}
      </div>
    </div>
  );
};

export default StudentFeeDetails;
